import {
  Box,
  FormLabel,
  FormControl,
  HStack,
  VStack,
  Input,
  Icon,
  Textarea,
  Text,
  Button,
  Badge,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { FaStickyNote, FaLock, FaUnlock, FaUser, FaArrowRight } from "react-icons/fa";
import { AnalystProto } from "build-ts/analyst";

interface AnalystConfigComponentProps {
  analyst: AnalystProto | undefined;
  setAnalyst: React.Dispatch<React.SetStateAction<AnalystProto | undefined>>;
  isCreatingNew?: boolean;
  hasDiscardedChanges?: boolean;
  setHasDiscardedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
  originalAnalyst?: AnalystProto; // for discarded changes
  onNavigateToDataAccess?: () => void; // function to navigate to data access component only when creating new analyst
}

const AnalystConfigComponent: React.FC<AnalystConfigComponentProps> = ({ 
  analyst,
  setAnalyst,
  isCreatingNew = false,
  hasDiscardedChanges = false,
  setHasDiscardedChanges,
  originalAnalyst,
  onNavigateToDataAccess
}) => {
  const [analystName, setAnalystName] = useState<string>(analyst?.header?.name || "");
  const [analystDescription, setAnalystDescription] = useState<string>(analyst?.header?.description || "");
  const [instructions, setInstructions] = useState<string>(analyst?.instructions || "");
  const [notes, setNotes] = useState<string>(analyst?.notes || "");
  const [isNotesEditable, setIsNotesEditable] = useState<boolean>(false);

  // Track the last loaded analyst
  const lastLoadedAnalystIdRef = useRef<string | undefined>(undefined);

  // Sync local state with analyst
  useEffect(() => {
    const currentAnalystId = analyst?.header?.id;
    
    // Only sync if:
    // 1. We're creating a new analyst (isCreatingNew), OR
    // 2. The analyst ID has actually changed (loading a different analyst)
    if (isCreatingNew) {
      // Reset form for new analyst
      lastLoadedAnalystIdRef.current = undefined;
      setAnalystName("");
      setAnalystDescription("");
      setInstructions("");
      setNotes("");
      setIsNotesEditable(false);
      return;
    }
    
    // Check if this is a different analyst than the one we last loaded
    if ((currentAnalystId && currentAnalystId !== lastLoadedAnalystIdRef.current && !hasDiscardedChanges)) {
      lastLoadedAnalystIdRef.current = currentAnalystId;
      
      setAnalystName(analyst?.header?.name || "");
      setAnalystDescription(analyst?.header?.description || "");
      setInstructions(analyst?.instructions || "");
      setNotes(analyst?.notes || "");
      setIsNotesEditable(false);
    }
  }, [analyst?.header?.id, isCreatingNew]);

  // Load original data when discarded changes are detected
  useEffect(() => {
    if(originalAnalyst && hasDiscardedChanges) {
      setAnalystName(originalAnalyst?.header?.name || "");
      setAnalystDescription(originalAnalyst?.header?.description || "");
      setInstructions(originalAnalyst?.instructions || "");
      setNotes(originalAnalyst?.notes || "");
      setIsNotesEditable(false);
      setHasDiscardedChanges?.(false);
    }
  },[hasDiscardedChanges]);

  // Update parent analyst state whenever form fields change
  useEffect(() => {
    setAnalyst((prev) => {
      if (!prev) {
        // If no previous analyst, create a new one with the form values
        return {
          header: {
            name: analystName,
            description: analystDescription,
          },
          role: "",
          instructions: instructions,
          notes: notes,
          scenarios: [],
        };
      }
      // Preserve role, data_scope, and scenarios from previous state
      return {
        ...prev,
        header: {
          ...prev.header,
          name: analystName,
          description: analystDescription,
        },
        instructions: instructions,
        notes: notes,
      };
    });
  }, [analystName, analystDescription, instructions, notes, setAnalyst]);

  return (
    <Box
      mx="auto"
      maxW="1200px"
      h="100%"
      overflowY="auto"
      px={8}
      py={6}
      sx={{
        "&::-webkit-scrollbar": {
          width: "6px"
        },
        "&::-webkit-scrollbar-track": {
          bg: "transparent"
        },
        "&::-webkit-scrollbar-thumb": {
          bg: "gray.300",
          borderRadius: "full"
        },
        "&::-webkit-scrollbar-thumb:hover": {
          bg: "gray.400"
        },
        // Firefox
        scrollbarWidth: "thin",
        scrollbarColor: "var(--chakra-colors-gray-300) transparent"
      }}
    >
      {/* Agent Configuration Section */}
      <Box
        bg="white"
        borderRadius="lg"
        border="1px"
        borderColor="gray.200"
        p={6}
        mb={6}
        boxShadow="sm"
      >
        <HStack spacing={2} mb={5}>
          <Icon as={FaUser} boxSize={5} color="purple.600" />
          <FormLabel fontSize="lg" mb={0} fontWeight="700" color="gray.800">
            Identity
          </FormLabel>
        </HStack>
        
        <VStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
              Name
            </FormLabel>
            <Input
              size="md"
              value={analystName}
              onChange={(e) => {
                setAnalystName(e.target.value);
              }}
              placeholder="Enter agent name"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
              }}
              fontWeight="500"
              bg="gray.50"
              _hover={{ bg: "white" }}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
              Description
            </FormLabel>
            <Input
              size="md"
              value={analystDescription}
              onChange={(e) => {
                setAnalystDescription(e.target.value);
              }}
              placeholder="Enter agent description"
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
              }}
              fontWeight="500"
              bg="gray.50"
              _hover={{ bg: "white" }}
            />
          </FormControl>
          
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
              Instructions
            </FormLabel>
            <Textarea
              size="md"
              value={instructions}
              onChange={(e) => {
                setInstructions(e.target.value);
              }}
              placeholder="Provide detailed guidelines and instructions for the agent..."
              rows={5}
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
              }}
              fontWeight="400"
              resize="vertical"
              bg="gray.50"
              _hover={{ bg: "white" }}
            />
          </FormControl>
        </VStack>
      </Box>

      {/* Navigate to Data Access button - Only show when creating new analyst */}
      {isCreatingNew && onNavigateToDataAccess && (
        <Box display="flex" justifyContent="center" mb={6}>
          <Button
            rightIcon={<Icon as={FaArrowRight} />}
            colorScheme="purple"
            size="lg"
            onClick={onNavigateToDataAccess}
            variant="outline"
          >
            Define Data Access
          </Button>
        </Box>
      )}

      {/* Agent's Scratchpad Section - Only show when editing existing analyst */}
      {!isCreatingNew && (
        <Box
          bg="white"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          p={6}
          mb={6}
          boxShadow="sm"
        >
          <HStack spacing={3} mb={3} justify="space-between">
            <HStack spacing={2}>
              <Icon as={FaStickyNote} boxSize={5} color="purple.600" />
              <FormLabel fontSize="lg" mb={0} fontWeight="700" color="gray.800">
                Scratchpad
              </FormLabel>
              <Badge colorScheme="purple" fontSize="xs" px={2}>
                Internal
              </Badge>
            </HStack>
            <Button
              size="sm"
              leftIcon={<Icon as={isNotesEditable ? FaUnlock : FaLock} />}
              onClick={() => setIsNotesEditable(!isNotesEditable)}
              colorScheme={isNotesEditable ? "green" : "gray"}
              variant={isNotesEditable ? "solid" : "outline"}
            >
              {isNotesEditable ? "Lock" : "Edit"}
            </Button>
          </HStack>
          
          <Text fontSize="xs" color="gray.500" mb={4} textAlign="left">
            This is the analyst's internal workspace for recording thoughts and instructions during their conversations with you. 
            {!isNotesEditable && " Click the lock button above to edit."}
          </Text>
          
          <FormControl>
            <Textarea
              size="md"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              placeholder="No notes yet..."
              rows={8}
              _focus={{
                borderColor: "purple.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
              }}
              fontWeight="400"
              resize="vertical"
              bg={isNotesEditable ? "gray.50" : "gray.100"}
              _hover={{ bg: isNotesEditable ? "white" : "gray.100" }}
              isReadOnly={!isNotesEditable}
              cursor={isNotesEditable ? "text" : "not-allowed"}
              opacity={isNotesEditable ? 1 : 0.8}
            />
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default AnalystConfigComponent;